import { SettingsService } from '@starsoft/common/services/SettingsService';
import { TransactionsThirdPartyAlertProps } from './props';
import styles from './styles.module.scss';
import { useLottiePlayer, useTranslation } from '@starsoft/common/hooks';

/**
 * TransactionsThirdPartyAlert Component
 * This component is responsible for displaying a third-party alert message with an animation.
 * It uses the Lottie animation library to render an animation and displays a message
 * indicating the third-party provider of the application.
 *
 * @param {TransactionsThirdPartyAlertProps} props - The properties for the TransactionsThirdPartyAlert component.
 * @param {boolean} props.hidden - A flag indicating whether the alert should be hidden.
 *
 * @returns {JSX.Element | null} The rendered component displaying the third-party alert or null if hidden.
 *
 * @example
 * // Usage example:
 * <TransactionsThirdPartyAlert hidden={false} />
 */
export default function TransactionsThirdPartyAlert({
  hidden,
}: TransactionsThirdPartyAlertProps) {
  // Hook to handle translations for the 'common' namespace
  const { t } = useTranslation('common');
  // Hook to manage the Lottie animation player
  const { lottieAnimationRef } = useLottiePlayer({
    path: '/safe.json',
    dependecyArray: [hidden],
  });

  // Return null if the alert is hidden
  if (hidden) {
    return null;
  }

  // Render the alert with the Lottie animation and provider message
  return (
    <div className={styles.card}>
      <div ref={lottieAnimationRef} className={styles.card__lottie} />
      <p>
        {t('third_party_alert', {
          app_provider: SettingsService.env.PROVIDER,
        })}
      </p>
    </div>
  );
}
